import { queryOptions, useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createAndSendMessage, createAndSendMultipleMessages, getAllStaffOids, getAllStudentOids, sendTeamsMessage } from "./api";
import { useUser } from "@features/user-account/queries";
import { usePortalModeStore } from "@stores/usePortalModeStore";
import toast from "react-hot-toast";
import { useId } from "@mantine/hooks";
import { TeamsMessage } from "@godalming/api-types";

export function useSendTeamsMessage() {

    const me = useUser().data?.id;
    const uuid = useId()

    return useMutation({
        mutationFn: async ({ recipient, content }: { recipient: string, content: any }) => {
            if (!me) {
                throw new Error('Unable to send Teams message without the current user id.');
            }

            await createAndSendMessage(recipient, content, me);
        },
        onMutate(variables, context) {
            const toastId = toast.loading('Sending message...', { id: uuid });
            return { toastId };
        },
        onSuccess(data, variables, onMutateResult, context) {
            toast.success('Message sent', { id: uuid });
        },
        onError(error, variables, onMutateResult, context) {
            toast.error('Failed to send message', { id: uuid });
        }
    });
}

export function useSendMultipleTeamsMessages() {

    const me = useUser().data?.id;

    const uuid = useId()

    return useMutation({
        mutationFn: async ({ recipients, content }: { recipients: string[], content: any }) => {
            if (!me) {
                throw new Error('Unable to send Teams messages without the current user id.');
            }

            await createAndSendMultipleMessages(recipients, content, me);
        },
        onMutate(variables, context) {
            const toastId = toast.loading('Sending message...', { id: uuid });
            return { toastId };
        },
        onSuccess(data, variables, onMutateResult, context) {
            toast.success('Message sent', { id: uuid });
        },
        onError(error, variables, onMutateResult, context) {
            toast.error('Failed to send message', { id: uuid });
        },
    });
}

export const allStaffOidsQueryOptions = () =>
    queryOptions({
        queryKey: ['staffOids'],
        queryFn: () => getAllStaffOids(),
        enabled: usePortalModeStore.getState().hostMode === 'staff',
        staleTime: Infinity,
    })

export function useStaffOids() {
    return useQuery(allStaffOidsQueryOptions())
}

export function useStaffOidsSuspense() {
    return useSuspenseQuery(allStaffOidsQueryOptions())
}

export const allStudentOidsQueryOptions = () => queryOptions({
    queryKey: ['communicationStudentDirectory'],
    queryFn: () => getAllStudentOids(),
    enabled: usePortalModeStore.getState().hostMode === 'staff',
    staleTime: Infinity,
})

export function useStudentOids() {
    return useQuery(allStudentOidsQueryOptions())
}

export function useStudentOidsSuspense() {
    return useSuspenseQuery(allStudentOidsQueryOptions())
}

export function useSendTeamsMessageProxy() {

    const uuid = useId()

    return useMutation({
        mutationFn: async (params: TeamsMessage) => {

            await sendTeamsMessage(params);
        },
        onMutate(variables, context) {
            const toastId = toast.loading('Sending message...', { id: uuid });
            return { toastId };
        },
        onSuccess(data, variables, onMutateResult, context) {
            toast.success('Message sent', { id: uuid });
        },
        onError(error, variables, onMutateResult, context) {
            toast.error('Failed to send message', { id: uuid });
        }
    });
}
